// The seed taps: which `one` taps may set the tempo.
//
// Before the count is locked every tap is a tap on count 1, and the first two (or more) of them give the
// tracker its tempo. A stray tap must not rescale the song, so a tap is judged against the ones before it:
//
//  - within SAME_TAP_S of a tap already held, it is the same tap (a double tap, two fingers) and is ignored;
//  - the first gap must be one 8-count, between MIN_EIGHT_COUNT_S and MAX_EIGHT_COUNT_S (300 down to about 53 beats per minute);
//  - later gaps must be a whole number of 8-counts of the tempo so far, give or take half a beat and 1% of the gap;
//  - a tap that is neither does not set the tempo: it becomes the first tap, and the ones before it are dropped. `judgeSeed` says why
//    (`soon`: it came less than one 8-count of the fastest tempo after the nearest tap; `unfit`: any other gap that is not a count of eight),
//    so the stage can tell the dancer in a sentence.
//
// Pure and deterministic, so the same log always gives the same seed (an old log with a bad tap in it recovers too).

import { fitTaps } from './grid.js';
import { PHRASE_BEATS } from './patterns.js';

/** One 8-count lasts at least this long (300 beats per minute). */
export const MIN_EIGHT_COUNT_S = 1.6;
/** One 8-count lasts at most this long (about 53 beats per minute). Raised from 6.0 s (80 BPM) in S4e: a song at 79 BPM has an 8-count of 6.09 s. */
export const MAX_EIGHT_COUNT_S = 9.0;
/** Two taps closer than this are one tap. */
export const SAME_TAP_S = 0.25;
/** A later tap may be this much of an 8-count off a whole number of them (half a beat), plus TEMPO_SLACK of the gap. */
const GAP_SLACK = 0.5 / PHRASE_BEATS;
const TEMPO_SLACK = 0.01;

export type SeedVerdict = 'first' | 'same' | 'accepted' | 'restart';

/** Why a tap started the seed again: `soon` (under MIN_EIGHT_COUNT_S after the nearest tap: a beat or two, not a count of eight) or `unfit` (any other gap that is not a count of eight). */
export type RestartReason = 'soon' | 'unfit';

export interface SeedJudgement {
  readonly verdict: SeedVerdict;
  /** Set when `verdict` is `restart`. */
  readonly reason: RestartReason | null;
}

/** The 8-count (seconds) the taps give, or null when they give none. */
export function eightCountOf(taps: readonly number[]): number | null {
  if (taps.length < 2) return null;
  try {
    return fitTaps(taps).map.beat * PHRASE_BEATS;
  } catch {
    return null;
  }
}

export const plausibleEightCount = (s: number | null): s is number => s !== null && Number.isFinite(s) && s >= MIN_EIGHT_COUNT_S - 1e-9 && s <= MAX_EIGHT_COUNT_S + 1e-9;

const verdictOnly = (verdict: Exclude<SeedVerdict, 'restart'>): SeedJudgement => ({ verdict, reason: null });
const restart = (reason: RestartReason): SeedJudgement => ({ verdict: 'restart', reason });

/** What a tap at time `t` is, given the seed taps held so far, and for a restart, why. */
export function judgeSeed(seed: readonly number[], t: number): SeedJudgement {
  if (!Number.isFinite(t)) return verdictOnly('same');
  if (seed.length === 0) return verdictOnly('first');
  let near = Infinity;
  for (const s of seed) near = Math.min(near, Math.abs(t - s));
  if (near < SAME_TAP_S) return verdictOnly('same');
  if (seed.length === 1) {
    if (near < MIN_EIGHT_COUNT_S) return restart('soon');
    return near <= MAX_EIGHT_COUNT_S ? verdictOnly('accepted') : restart('unfit');
  }
  const misfit = restart(near < MIN_EIGHT_COUNT_S ? 'soon' : 'unfit');
  const phrase = eightCountOf(seed);
  if (phrase === null) return misfit;
  const k = Math.round(near / phrase);
  if (k < 1 || Math.abs(near - k * phrase) > GAP_SLACK * phrase + TEMPO_SLACK * near) return misfit;
  return plausibleEightCount(eightCountOf([...seed, t])) ? verdictOnly('accepted') : misfit;
}

/** What a tap at time `t` is, given the seed taps held so far. */
export const judgeSeedTap = (seed: readonly number[], t: number): SeedVerdict => judgeSeed(seed, t).verdict;

/** The items (taps with their own identity) that seed the tempo, by the same rule as `seedTaps`; `time` reads an item's tap time. */
export function seedItems<T>(items: readonly T[], time: (item: T) => number): T[] {
  let seed: T[] = [];
  for (const item of items) {
    const v = judgeSeedTap(seed.map(time), time(item));
    if (v === 'first' || v === 'accepted') seed.push(item);
    else if (v === 'restart') seed = [item];
  }
  return seed;
}

/** The taps that seed the tempo, from the `one` taps in the order they were made. */
export function seedTaps(times: readonly number[]): number[] {
  return seedItems(times, (t) => t);
}
